/**
 * Sidebar Page Access Hook
 *
 * Provides efficient page access checking for sidebar navigation.
 * Uses bulk loading to minimize API calls.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '@/services/apiClient';
import { CBORoleType } from '@/types/cboRoles';

// ============================================================================
// TYPES
// ============================================================================

interface PageAccessResult {
  has_access: boolean;
  access_source: string;
  enabled: boolean;
  display_behavior: string;
}

interface SidebarPageAccessState {
  accessMap: Record<string, PageAccessResult>;
  loading: boolean;
  error: string | null;
}

// ============================================================================
// CACHE
// ============================================================================

const sidebarAccessCache = new Map<string, {
  data: Record<string, PageAccessResult>;
  timestamp: number;
}>();

const CACHE_TTL = 60000; // 1 minute

function getCacheKey(orgId: number, roleType?: string): string {
  return `sidebar:${orgId}:${roleType || 'default'}`;
}

// ============================================================================
// PAGE KEY MAPPINGS
// ============================================================================

/**
 * Maps navigation item IDs to page keys
 * This allows the sidebar to check page access using the page_key from the database
 * NOTE: Navigation item IDs must match exactly what's used in SidebarNavigation.tsx
 */
export const NAV_ITEM_TO_PAGE_KEY: Record<string, string> = {
  // Core
  'dashboard': 'dashboard',
  'home': 'home',

  // V2 hub pages (map to underlying page_keys)
  'v2-home': 'home',
  'v2-referrals': 'care.referrals',
  'v2-calendar': 'events.calendar',
  'v2-comms': 'communications.hub',
  'v2-supervisor': 'dashboards.supervisor',
  'contacts': 'contacts.list',

  // Members
  'members': 'members.list',
  'member-list': 'members.list',
  'member-profile': 'members.profile',
  'member-add': 'members.create',

  // Enrollments
  'enrollments': 'enrollments.list',
  'enrollment-queue': 'enrollments.queue',
  'enrollment-new': 'enrollments.create',
  'enrollment-pending': 'enrollments.pending',

  // Eligibility Verification
  'eligibility-screening': 'eligibility.screening',
  'eligibility-verification': 'eligibility.verification',
  'eligibility-direct': 'eligibility.direct_verification',
  // TRUS-724: same page (role) access as member verification (it reads the same
  // checks). The org gate is separate: TRUS-739 requires the ffs_transition
  // capability, checked in app.tsx, lib/navigablePages and the API.
  'ffs-transition-cohort': 'eligibility.verification',
  'eligibility-credentials': 'eligibility.credentials',

  // Care Plans
  'care-plans': 'care_plans.list',

  // Communications
  'communications': 'communications.inbox',
  'inbox': 'communications.inbox',
  'groups': 'communications.groups',
  'campaigns': 'communications.campaigns',
  'templates': 'communications.templates',
  'broadcast': 'communications.broadcast',
  'scheduled': 'communications.scheduled',
  'voice': 'communications.voice',
  'bulk-communications': 'communications.broadcast',
  'communication-templates': 'communications.templates',
  'communication-history': 'communications.history',
  'workspace-messaging': 'communications.workspace',

  // Analytics
  'analytics': 'analytics.dashboard',
  'reports': 'analytics.reports',
  'exports': 'analytics.exports',
  'population-health': 'analytics.population',
  'ai-orchestration': 'analytics.ai_orchestration',
  'external-services': 'admin.settings',

  // Billing
  'billing-activities': 'billing.activities',
  'billing-analytics': 'billing.analytics',
  'billing-claims': 'billing.claims',
  'reimbursement': 'billing.reimbursement',
  'cbo-reimbursement': 'billing.reimbursement',
  'reconciliation': 'billing.reconciliation',
  'clearinghouse-settings': 'billing.clearinghouse',

  // Incentives
  'incentives': 'incentives.dashboard',
  'milestones': 'incentives.milestones',
  'pending-approvals': 'incentives.pending',
  'program-incentive-dashboard': 'incentives.dashboard',
  'milestone-management': 'incentives.milestones',
  'program-milestone-config': 'incentives.config',
  'member-milestone-progress': 'incentives.member_progress',
  'pending-milestone-approvals': 'incentives.pending',
  'member-payouts': 'incentives.payouts',

  // Care Coordination & Referrals
  'referrals': 'care.referrals',
  'network': 'care.network',
  'providers': 'care.providers',
  'create-referral': 'care.referrals',
  'provider-directory': 'care.providers',
  'provider-performance': 'care.provider_performance',
  'network-overview': 'care.network',
  'care-coordination-hub': 'care.coordination',
  'behavioral-assessment': 'care.behavioral_assessment',
  'social-needs-screener': 'care.social_needs_screener',
  // CCH pages: access controlled by capability gating in SidebarNavigation, not page_capabilities
  'matching': 'care.matching',
  'directory': 'care.directory',
  'unmatched': 'care.unmatched',

  // TCO (Trust Care Organization)
  'tco-dashboard': 'tco.dashboard',
  'tco-referrals': 'tco.service_referrals',
  'tco-workflows': 'tco.workflows',
  'tco-settings': 'tco.settings',

  // Programs
  'programs': 'programs.list',
  // 'program-billing' — removed to ensure Billing Configuration section is always visible
  'program-consent-management': 'programs.consent',
  'program-marketplace': 'programs.marketplace',
  'program-intelligence': 'programs.intelligence',

  // Workflows
  'workflows': 'workflows.list',
  'workflow-builder': 'workflows.builder',
  'workflow-instances': 'workflows.instances',
  'automation-rules': 'workflows.automation',

  // Forms
  'form-library': 'forms.library',
  'form-templates': 'forms.templates',
  'form-analytics': 'forms.analytics',

  // Team
  'team': 'team.list',
  'team-assignments': 'team.assignments',
  'user-management': 'admin.users',

  // Audits & Compliance
  'audits': 'audits.queue',
  'audit-review': 'audits.queue',
  'compliance': 'audits.compliance',
  'cbo-compliance-dashboard': 'audits.compliance',
  'compliance-documents': 'audits.documents',

  // Administration
  'settings': 'admin.settings',
  'capabilities': 'admin.capabilities',
  'access-control': 'admin.role_access',
  'feature-capabilities': 'admin.feature_capabilities',
  'page-capabilities': 'admin.page_access',
  'role-page-access': 'admin.role_access',
  'nav-layout': 'admin.nav_layout',
  'page-access': 'admin.page_access',
  'role-access': 'admin.role_access',
  'users': 'admin.users',
  'roles': 'admin.roles',
  'consent': 'admin.consent',
  'workbench': 'admin.workbench',
  'member-fields': 'admin.member_fields',
  'organization-roles': 'admin.organization_roles',
  'config-migration': 'admin.config_migration',
  'translation-management': 'admin.translations',
  'terminology-glossary': 'admin.terminology',
  'rag-admin': 'admin.rag',
  'field-mapping': 'admin.field_mapping',

  // Health Information Exchange (LANES LA Integration)
  // NOTE: Not mapped to page keys — access controlled via requiresRole only.
  // Add page keys here once admin.saml_sso, admin.hl7_config, admin.ihe_config
  // are registered in the page_capabilities table on the backend.

  // Employer
  'employer-dashboard': 'employer.dashboard',
  'employer-benefits': 'employer.benefits',
  'employer-employees': 'employer.employees',
  'employer-leave': 'employer.leave',
  'employer-analytics': 'employer.analytics',
  'employer-settings': 'employer.settings',
  'employer-benefits-management': 'employer.benefits',
  'employer-workflow-orchestration': 'employer.workflows',
  'employee-retention': 'employer.retention',

  // Employee Portal
  'employee-dashboard': 'employee.dashboard',
  'employee-benefits': 'employee.benefits',
  'employee-incentives': 'employee.incentives',
  'employee-messages': 'employee.messages',

  // Medicaid
  'work-requirements': 'medicaid.work_requirements',
  'medicaid-compliance': 'medicaid.compliance',
  'work-verification': 'medicaid.verification',
  'cbo-work-verification': 'medicaid.verification',
  'cbo-data-entry': 'medicaid.data_entry',

  // Telehealth
  'telehealth': 'telehealth.dashboard',
  'telehealth-reports': 'telehealth.reports',

  // Workforce Development
  'workforce-dashboard': 'workforce.dashboard',
  'workforce-roster': 'workforce.roster',
  'workforce-candidates': 'workforce.candidates',
  'workforce-certifications': 'workforce.certifications',
  'workforce-curricula': 'workforce.curricula',
  'workforce-mentoring': 'workforce.mentoring',
  'workforce-roles': 'workforce.roles',
  'workforce-skills-gap': 'workforce.skills_gap',
  'workforce-case-management': 'workforce.case_management',
  'workforce-cases': 'workforce.case_management',  // Actual sidebar nav ID
  'workforce-assessments': 'workforce.assessments',
  'workforce-ai-assistant': 'workforce.ai_assistant',
  'workforce-unified': 'workforce.unified_dashboard',
  'workforce-unified-dashboard': 'workforce.unified_dashboard',  // Actual sidebar nav ID

  // WeaveIn Platform (Marketing & Sales)
  'weavein-dashboard': 'weavein.dashboard',
  'weavein-intelligence': 'weavein.intelligence',
  'weavein-pipeline': 'weavein.pipeline',
  'weavein-webpresence': 'weavein.webpresence',
  'platform-dashboard': 'weavein.dashboard',
  'platform-intelligence': 'weavein.intelligence',
  'platform-lead-builder': 'weavein.lead_builder',
  'platform-contacts': 'weavein.contacts',
  'platform-sales': 'weavein.sales',
  'platform-marketing': 'weavein.marketing',
  'platform-marketplace': 'weavein.marketplace',
  'platform-invoices': 'weavein.invoices',
  'platform-web-presence': 'weavein.webpresence',
  'platform-visitor-id': 'weavein.visitor_id',
  'platform-cold-outreach': 'weavein.cold_outreach',
  'platform-revops': 'weavein.revops',
  'platform-support': 'weavein.support',

  // Sponsor Portal
  'sponsor-dashboard': 'sponsor.dashboard',
  'sponsor-retention': 'sponsor.retention',
  'sponsor-terms': 'sponsor.terms',

  // CBO Management
  'cbo-management': 'cbo.management',
  'health-plans': 'cbo.health_plans',
  'resources-management': 'cbo.resources',

  // Cards & Payments
  'card-management': 'payments.cards',
  'transactions': 'payments.transactions',
  'my-card': 'payments.my_card',
  'fund-deposits': 'payments.deposits',

  // Media & Content
  'media-library': 'content.media',
  // 'interactive-content' — no page key, visible to all comm users
  'webinar-management': 'content.webinars',
  'campaign-templates': 'content.campaign_templates',
  'social-media-connections': 'content.social_media',

  // Events & Calendar
  'calendar': 'events.calendar',
  'event-registrations': 'events.registrations',

  // Special Dashboards
  'viewer-capabilities': 'dashboards.viewer',
  'livefeed': 'dashboards.livefeed',
  'capacity': 'dashboards.capacity',
  'user-goals': 'dashboards.goals',
  'member-retention': 'analytics.retention',

  // Self-service
  'self-signup': 'self_service.signup',

  // Operations
  'action-tracker': 'operations.action_tracker',
  'actions': 'operations.action_tracker',  // Actual sidebar nav ID

  // Referrals (specific mappings for care coordination)
  'queue': 'care.referrals',  // Referrals queue in care coordination section
  'workspace': 'tco.service_referrals.workspace',  // Referral workspace
};

/**
 * Get all page keys for sidebar navigation
 */
export function getAllSidebarPageKeys(): string[] {
  return [...new Set(Object.values(NAV_ITEM_TO_PAGE_KEY))];
}

/**
 * Get page key for a navigation item ID
 */
export function getPageKeyForNavItem(navItemId: string): string | undefined {
  return NAV_ITEM_TO_PAGE_KEY[navItemId];
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for checking page access for sidebar navigation
 *
 * @param organizationId - The organization ID
 * @param roleType - Optional role type override
 * @returns Object with access map, loading state, and helper functions
 *
 * @example
 * ```tsx
 * const { hasPageAccess, loading } = useSidebarPageAccess(orgId);
 *
 * // In navigation filtering
 * const filteredItems = navItems.filter(item => {
 *   const pageKey = getPageKeyForNavItem(item.id);
 *   return !pageKey || hasPageAccess(pageKey);
 * });
 * ```
 */
export function useSidebarPageAccess(
  organizationId: number | null,
  roleType?: CBORoleType
): {
  accessMap: Record<string, PageAccessResult>;
  loading: boolean;
  error: string | null;
  hasPageAccess: (pageKeyOrNavItemId: string) => boolean;
  getDisplayBehavior: (pageKeyOrNavItemId: string) => string;
  refetch: () => void;
} {
  const [state, setState] = useState<SidebarPageAccessState>({
    accessMap: {},
    loading: true,
    error: null,
  });

  // Use ref to store accessMap for stable callback references
  // This prevents infinite re-render loops when callbacks are used in effects
  const accessMapRef = useRef<Record<string, PageAccessResult>>({});
  accessMapRef.current = state.accessMap;

  const fetchAccess = useCallback(async () => {
    if (!organizationId) {
      setState({ accessMap: {}, loading: false, error: null });
      return;
    }

    const cacheKey = getCacheKey(organizationId, roleType);

    // Check cache
    const cached = sidebarAccessCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setState({ accessMap: cached.data, loading: false, error: null });
      return;
    }

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Get all page keys needed for sidebar
      const pageKeys = getAllSidebarPageKeys();

      const url = roleType
        ? `/api/organizations/${organizationId}/page-access/bulk-check?role_type=${roleType}`
        : `/api/organizations/${organizationId}/page-access/bulk-check`;

      const response = await apiClient.post(url, pageKeys, {
        headers: { 'X-CBO-ID': organizationId.toString() }
      });

      const data: Record<string, PageAccessResult> = response.data;

      // Update cache
      sidebarAccessCache.set(cacheKey, {
        data,
        timestamp: Date.now(),
      });

      setState({ accessMap: data, loading: false, error: null });
    } catch (error: any) {
      console.error('Error loading sidebar page access:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.detail || error.message || 'Failed to load page access'
      }));
    }
  }, [organizationId, roleType]);

  useEffect(() => {
    fetchAccess();
  }, [fetchAccess]);

  // Listen for permission updates to auto-refresh
  useEffect(() => {
    const handlePermissionUpdate = (event: CustomEvent<{ orgId: number }>) => {
      if (event.detail.orgId === organizationId) {
        // Clear cache and refetch
        clearSidebarAccessCache(organizationId);
        fetchAccess();
      }
    };

    window.addEventListener('pagePermissionsUpdated', handlePermissionUpdate as EventListener);
    return () => {
      window.removeEventListener('pagePermissionsUpdated', handlePermissionUpdate as EventListener);
    };
  }, [organizationId, fetchAccess]);

  // Helper to check if a page or nav item has access
  // Uses ref to prevent callback recreation on every accessMap change
  const hasPageAccess = useCallback((pageKeyOrNavItemId: string): boolean => {
    const accessMap = accessMapRef.current;

    // First try as a page key directly
    if (accessMap[pageKeyOrNavItemId]) {
      return accessMap[pageKeyOrNavItemId].has_access;
    }

    // Then try mapping from nav item ID
    const pageKey = NAV_ITEM_TO_PAGE_KEY[pageKeyOrNavItemId];
    if (pageKey && accessMap[pageKey]) {
      return accessMap[pageKey].has_access;
    }

    // If not found, default to allowing access
    // This handles pages not in the page_capabilities registry
    return true;
  }, []); // Empty deps - callback is stable, uses ref for current data

  // Helper to get display behavior
  // Uses ref to prevent callback recreation on every accessMap change
  const getDisplayBehavior = useCallback((pageKeyOrNavItemId: string): string => {
    const accessMap = accessMapRef.current;

    if (accessMap[pageKeyOrNavItemId]) {
      return accessMap[pageKeyOrNavItemId].display_behavior;
    }

    const pageKey = NAV_ITEM_TO_PAGE_KEY[pageKeyOrNavItemId];
    if (pageKey && accessMap[pageKey]) {
      return accessMap[pageKey].display_behavior;
    }

    return 'hide';
  }, []); // Empty deps - callback is stable, uses ref for current data

  return {
    accessMap: state.accessMap,
    loading: state.loading,
    error: state.error,
    hasPageAccess,
    getDisplayBehavior,
    refetch: fetchAccess,
  };
}

/**
 * Clear the sidebar access cache
 */
export function clearSidebarAccessCache(orgId?: number): void {
  if (orgId) {
    // Clear just for this org
    for (const key of sidebarAccessCache.keys()) {
      if (key.startsWith(`sidebar:${orgId}:`)) {
        sidebarAccessCache.delete(key);
      }
    }
  } else {
    // Clear all
    sidebarAccessCache.clear();
  }
}

export default useSidebarPageAccess;
